import React, { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { valibotResolver } from '@hookform/resolvers/valibot';
import toast from 'react-hot-toast';
import { OfferingFormProps } from './types';
import { offeringFormSchema } from './schema';
import type { OfferingFormValues } from './schema';
import { styles } from './styles';
import { Modal, Button, Input, Checkbox, CheckboxGroup, Alert } from '@v2/components';
import type { CheckboxGroupChangePayload } from '@v2/components';
import {
  BitwerxOffering,
  PartnerOffering,
  OfferingsListItem,
  UpdateOfferingPayload,
  CreateOfferingPayload,
} from '@v2/types';
import { useUpdateOffering, useCreateOffering } from '@v2/hooks';

/** Build offeringsList from bitwerxOfferings and initial selection (once). */
function buildOfferingsList(
  bitwerxOfferings: BitwerxOffering[],
  offeringIds: string[],
  subOfferingIds: string[],
): OfferingsListItem[] {
  return bitwerxOfferings.map(offering => ({
    id: offering.id,
    name: offering.name,
    isEnabled: offeringIds.includes(offering.id),
    subOfferings: offering.subOfferings.map(sub => ({
      id: sub.id,
      name: sub.displayName || sub.name,
      isEnabled: subOfferingIds.includes(sub.id),
    })),
  }));
}

/** Derive PartnerOffering ids from offeringsList for the payload. */
function deriveIdsFromOfferingsList(offeringsList: OfferingsListItem[]) {
  const bitwerxOfferingIds = offeringsList.filter(o => o.isEnabled).map(o => o.id);
  const bitwerxSubOfferingIds = offeringsList.flatMap(o =>
    o.subOfferings.filter(s => s.isEnabled).map(s => s.id),
  );
  return { bitwerxOfferingIds, bitwerxSubOfferingIds };
}

const OfferingForm: React.FC<OfferingFormProps> = ({
  bitwerxOfferings,
  partnerId,
  partnerOffering,
  onClose,
  onCompleted,
}) => {
  const { updateOffering, loading: updateLoading, error: updateError } = useUpdateOffering();
  const { createOffering, loading: createLoading, error: createError } = useCreateOffering();
  const isEditMode = !!partnerOffering;
  const loading = isEditMode ? updateLoading : createLoading;
  const error = isEditMode ? updateError : createError;

  const {
    register,
    handleSubmit: rhfHandleSubmit,
    watch,
    formState: { errors },
  } = useForm<OfferingFormValues>({
    resolver: valibotResolver(offeringFormSchema),
    defaultValues: { name: partnerOffering?.name ?? '' },
  });
  const name = watch('name');

  const [offeringsList, setOfferingsList] = useState<OfferingsListItem[]>(() =>
    buildOfferingsList(
      bitwerxOfferings,
      partnerOffering?.bitwerxOfferingIds ?? [],
      partnerOffering?.bitwerxSubOfferingIds ?? [],
    ),
  );

  const handleOfferingGroupChange = useCallback(
    (offering: BitwerxOffering, { parentChecked, options }: CheckboxGroupChangePayload) => {
      const offeringId = offering.id;
      setOfferingsList(prev =>
        prev.map(item =>
          item.id !== offeringId
            ? item
            : {
                ...item,
                isEnabled: parentChecked,
                subOfferings: item.subOfferings.map(sub => ({
                  ...sub,
                  isEnabled: options.find(o => o.id === sub.id)?.checked ?? false,
                })),
              },
        ),
      );
    },
    [],
  );

  const handleParentOfferingChange = useCallback((offeringId: string, checked: boolean) => {
    setOfferingsList(prev =>
      prev.map(item => (item.id === offeringId ? { ...item, isEnabled: checked } : item)),
    );
  }, []);

  const onSubmit = async (data: OfferingFormValues) => {
    const { bitwerxOfferingIds, bitwerxSubOfferingIds } = deriveIdsFromOfferingsList(offeringsList);

    if (partnerOffering) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { offerings: _offerings, ...rest } = partnerOffering as PartnerOffering & {
        offerings?: unknown;
      };
      const payload: UpdateOfferingPayload = {
        ...rest,
        name: data.name,
        bitwerxOfferingIds,
        bitwerxSubOfferingIds,
        offeringsList,
      };
      const success = await updateOffering(partnerOffering.partnerId, payload);
      if (success) {
        toast.success('Offerings updated successfully');
        onCompleted?.();
      } else {
        toast.error('Failed to update offering');
      }
    } else {
      const payload: CreateOfferingPayload = {
        partnerId,
        name: data.name.trim(),
        offeringsList,
      };
      const success = await createOffering(partnerId, payload);
      if (success) {
        toast.success('Offerings created successfully');
        onCompleted?.();
      } else {
        toast.error('Failed to create offering');
      }
    }
  };

  const title = partnerOffering ? 'Edit Offerings' : 'Add Offerings';
  const canSubmit = partnerOffering ? !loading : !!partnerId && !!name.trim() && !loading;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={title}
      size="50vw"
      variant="side"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={rhfHandleSubmit(onSubmit)}
            disabled={!canSubmit}
            isLoading={loading}>
            Submit
          </Button>
        </>
      }>
      <form onSubmit={rhfHandleSubmit(onSubmit)} className={styles.formContainer}>
        {error && (
          <Alert variant="error" className={styles.errorAlert}>
            {error}
          </Alert>
        )}
        <Input
          {...register('name')}
          label="Name"
          placeholder="Offerings name"
          error={errors.name?.message}
          required
        />
        <div className={styles.offeringsSection}>
          <span className={styles.offeringsLabel}>Offerings</span>
          <div className={styles.offeringsScrollContainer}>
            {bitwerxOfferings
              .sort((a, b) => a.name.localeCompare(b.name))
              .map(offering => {
                const listItem = offeringsList.find(o => o.id === offering.id);
                if (!listItem) return null;
                return (
                  <div key={offering.id} className={styles.offeringGroup}>
                    {offering.subOfferings.length > 0 ? (
                      <CheckboxGroup
                        label={offering.name}
                        options={listItem.subOfferings
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map(sub => ({
                            id: sub.id,
                            label: sub.name,
                            checked: sub.isEnabled,
                          }))}
                        onChange={payload => handleOfferingGroupChange(offering, payload)}
                      />
                    ) : (
                      <Checkbox
                        id={offering.id}
                        label={offering.name}
                        checked={listItem.isEnabled}
                        onChange={checked => handleParentOfferingChange(offering.id, checked)}
                      />
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default OfferingForm;
